import {EditorView} from "prosemirror-view";
import {Node, ResolvedPos} from "prosemirror-model";
import { nanoid } from 'nanoid';


import { IStorageService } from "../../platform/services/storage/StorageService";
import {getExtensionFromMimeType} from "./getExtensionFromMimeType";
import {setSelectionSafe} from "../../Editor/module/commands/triggers/utils";

export interface UploadCallbacks {
    completeCallback?: () => void;
    progressCallback?: (progress: { loaded: number; total: number }) => void;
    errorCallback?: (err: Error) => void;
}

export const uploadFileToS3 = async (
    file: File, 
    storageService: IStorageService,
    tenantId: string,
    callbacks?: UploadCallbacks
) => {
    const mimeType = file.type;
    const extension = getExtensionFromMimeType(mimeType);
    const fileName = `${nanoid(6)}.${extension}`;
    
    try {
        const result = await storageService.upload(file, fileName, { 
            contentType: mimeType, 
            tenantId 
        });
        
        if (callbacks?.completeCallback) {
            callbacks.completeCallback();
        }
        
        return {
            s3FileName: result.path.replace(`${tenantId}/`, ''), // Remove tenant prefix for backward compatibility
            mimeType: mimeType,
        };
    } catch (error) {
        if (callbacks?.errorCallback) {
            callbacks.errorCallback(error as Error);
        }
        throw error;
    }
}


export const placeImage = (view: EditorView, imageNode: Node, pos?: number | ResolvedPos, length: number = 0) => {
    let tr = view.state.tr;
    let targetPos: number;

    if (pos && typeof pos === 'number') {
        tr = tr.replaceRangeWith(pos, pos + length, imageNode);
        targetPos = pos + imageNode.nodeSize;
    } else if (pos) {
        const $pos = pos as ResolvedPos;
        tr = tr.replaceRangeWith($pos.start(), length ? $pos.start() + length : $pos.end(), imageNode);
        targetPos = tr.mapping.map($pos.after());
    } else {
        tr = tr.replaceSelectionWith(imageNode);
        targetPos = tr.selection.$to.pos;
    }

    tr = setSelectionSafe(tr, targetPos);
    return tr;
}

export const placeFile = (view: EditorView, fileNode: Node, pos?: number | ResolvedPos, length: number = 0) => {
    let targetPos: number;
    let tr = view.state.tr;

    if (pos && typeof pos === 'number') {
        tr = tr.replaceRangeWith(pos, pos + length, fileNode);
        targetPos = pos + fileNode.nodeSize;
    } else if (pos) {
        tr = tr.replaceRangeWith((pos as ResolvedPos).before(), (pos as ResolvedPos).after(), fileNode);
        targetPos = (pos as ResolvedPos).before() + fileNode.nodeSize;
    } else {
        tr = view.state.tr.replaceSelectionWith(fileNode);
        targetPos = tr.selection.$to.pos;
    }

    tr = setSelectionSafe(tr, targetPos);
    return tr;
}

export const isImage = (mimeType: string) => {
    return mimeType.split('/')[0] === 'image';
}